import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, X, Users, Crown, Baby, Move } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { SeatingToolbar, MenuEditor, GuestDetailRow, SeatRequests } from "../../components/seating/SeatingExtras";

const SHAPES = [["round", "Round"], ["rectangle", "Rectangle"], ["square", "Square"], ["long", "Long"]];

export default function SeatingChartPage() {
  const [data, setData] = useState({ tables: [], unassigned: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", capacity: 8, shape: "round", x: 100, y: 100, is_kids: false, is_vip: false });
  const [busy, setBusy] = useState(false);
  const [draggingTable, setDraggingTable] = useState(null);
  const [draggingGuest, setDraggingGuest] = useState(null);
  const canvasRef = useRef(null);
  const [details, setDetails] = useState(null);
  const [requests, setRequests] = useState([]);

  async function load() {
    const { data } = await api.get("/tables");
    setData(data);
    api.get("/tables/details").then((r) => setDetails(r.data)).catch(() => {});
    api.get("/seating/requests").then((r) => setRequests(r.data)).catch(() => {});
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function add(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tables", form);
      setForm({ name: "", capacity: 8, shape: "round", x: 100 + Math.random()*400, y: 100 + Math.random()*200, is_kids: false, is_vip: false });
      setOpen(false);
      toast.success("Table added");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  async function delTable(id) {
    if (!confirm("Delete this table? Guests will be unassigned.")) return;
    await api.delete(`/tables/${id}`);
    load();
  }

  async function patchTable(id, patch) {
    await api.patch(`/tables/${id}`, patch);
  }

  async function assignGuest(tableId, guestId) {
    try {
      const { data: r } = await api.post(`/tables/${tableId}/assign-group/${guestId}`);
      if (r.moved > 1) toast.success(`Seated ${r.group} together (${r.moved} guests)`);
      if (r.over_capacity) toast.warning(`That table is now over capacity (${r.seated} seated)`);
    } catch (e) { toast.error(formatApiError(e)); }
    load();
  }

  async function unassignGuest(guestId) {
    await api.post(`/tables/unassign/${guestId}`);
    load();
  }

  function onMouseDown(e, table) {
    const rect = canvasRef.current.getBoundingClientRect();
    setDraggingTable({ id: table.table_id, offsetX: e.clientX - rect.left - table.x, offsetY: e.clientY - rect.top - table.y });
  }

  function onMouseMove(e) {
    if (!draggingTable) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(10, Math.min(rect.width - 130, e.clientX - rect.left - draggingTable.offsetX));
    const y = Math.max(10, Math.min(rect.height - 130, e.clientY - rect.top - draggingTable.offsetY));
    setData((d) => ({
      ...d,
      tables: d.tables.map((t) => t.table_id === draggingTable.id ? { ...t, x, y } : t),
    }));
  }

  async function onMouseUp() {
    if (draggingTable) {
      const t = data.tables.find((t) => t.table_id === draggingTable.id);
      if (t) await patchTable(t.table_id, { x: t.x, y: t.y });
    }
    setDraggingTable(null);
  }

  function onTableDrop(e, tableId) {
    e.preventDefault();
    const guestId = e.dataTransfer.getData("guest_id");
    if (guestId) assignGuest(tableId, guestId);
  }

  return (
    <div data-testid="seating-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Seating</p>
          <h1 className="font-serif font-light text-4xl">Drag-drop chart builder</h1>
          <p className="text-sm text-[var(--muted)] mt-2">Drag tables to position them. Drag guest names onto tables to assign.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-table-btn" className="btn-gold"><Plus className="w-4 h-4" /> Add Table</button>
      </div>

      <SeatingToolbar d={details} slug={details?.slug} reload={load} />
      {details && <MenuEditor key={details.meal_options.join("|")} options={details.meal_options} reload={load} />}
      {details?.split_groups?.length > 0 && <p className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2" data-testid="split-groups-warning">Split across tables: {details.split_groups.join(", ")}. Drag one member onto a table to seat the whole group together.</p>}
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div
          ref={canvasRef}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          className="relative bg-[var(--bg-warm)] rounded-2xl border border-[var(--gold-line)] overflow-hidden"
          style={{ height: "600px", backgroundImage: "radial-gradient(circle, rgba(200,169,107,0.15) 1px, transparent 1px)", backgroundSize: "24px 24px" }}
        >
          {data.tables.length === 0 && <p className="absolute inset-0 flex items-center justify-center text-[var(--muted)]">No tables yet — add your first table.</p>}
          {data.tables.map((t) => {
            const seated = (t.assigned_guests || []).length;
            const overcap = seated > t.capacity;
            const size = t.shape === "long" ? { w: 160, h: 80 } : t.shape === "rectangle" ? { w: 140, h: 90 } : t.shape === "square" ? { w: 110, h: 110 } : { w: 120, h: 120 };
            const radius = t.shape === "round" ? "50%" : "1rem";
            return (
              <div
                key={t.table_id}
                data-testid={`table-${t.table_id}`}
                onMouseDown={(e) => onMouseDown(e, t)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onTableDrop(e, t.table_id)}
                className="absolute cursor-move bg-white border-2 flex flex-col items-center justify-center shadow-sm select-none"
                style={{
                  left: t.x, top: t.y, width: size.w, height: size.h, borderRadius: radius,
                  borderColor: t.is_vip ? "var(--gold)" : t.is_kids ? "#f4d480" : "var(--line)",
                  background: overcap ? "#fee" : (t.is_vip ? "#fcf2dc" : "white"),
                }}
              >
                <p className="font-serif text-sm font-medium text-center px-1">{t.name}</p>
                <p className="text-[10px] text-[var(--muted)]">{seated}/{t.capacity}</p>
                {seated > 0 && details && <p className="text-[9px] text-[var(--muted)] text-center px-1 leading-tight" data-testid={`table-meals-${t.table_id}`}>{Object.entries((t.assigned_guests || []).reduce((m, g) => { const k = details.guests.find((x) => x.guest_id === g.guest_id)?.meal_preference; if (k) m[k] = (m[k] || 0) + 1; return m; }, {})).map(([k, v]) => `${v} ${k}`).join(" · ")}</p>}
                {details && (() => { const n = (t.assigned_guests || []).filter((g) => details.guests.find((x) => x.guest_id === g.guest_id)?.dietary).length; return n ? <p className="text-[9px] text-amber-700 font-medium" title={(t.assigned_guests || []).map((g) => { const x = details.guests.find((y) => y.guest_id === g.guest_id); return x?.dietary ? `${g.name}: ${x.dietary}` : null; }).filter(Boolean).join("\n")} data-testid={`table-dietary-${t.table_id}`}>{n} dietary note{n > 1 ? "s" : ""}</p> : null; })()}
                {t.is_vip && <Crown className="w-3 h-3 text-[var(--gold)] absolute top-1 right-1" />}
                {t.is_kids && <Baby className="w-3 h-3 text-[#b09020] absolute top-1 left-1" />}
                {seated > 0 && (
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 translate-y-full mt-1 bg-white border border-[var(--line)] rounded text-[10px] px-2 py-1 shadow-sm whitespace-nowrap max-w-[180px] truncate opacity-0 hover:opacity-100 transition pointer-events-none">
                    {(t.assigned_guests || []).map(g => g.name).join(", ")}
                  </div>
                )}
                <button onMouseDown={(e) => e.stopPropagation()} onClick={() => delTable(t.table_id)} className="absolute -top-2 -right-2 bg-white border border-[var(--line)] rounded-full p-1"><Trash2 className="w-3 h-3 text-red-500" /></button>
              </div>
            );
          })}
        </div>

        <div className="bg-white border border-[var(--line)] rounded-2xl p-4 max-h-[600px] flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Unassigned</p>
            <span className="text-xs text-[var(--muted)]">{data.unassigned?.length || 0}</span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1">
            {(data.unassigned || []).map((g) => (
              <div
                key={g.guest_id}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("guest_id", g.guest_id)}
                className="px-3 py-2 rounded-lg bg-[var(--bg-warm)] cursor-grab active:cursor-grabbing flex items-center gap-2"
              >
                <Move className="w-3 h-3 text-[var(--muted)]" />
                <span className="text-sm flex-1">{g.name}</span>
                {details?.guests?.find((x) => x.guest_id === g.guest_id)?.seat_group && <span className="text-[10px] text-[var(--gold)] truncate max-w-[90px]">{details.guests.find((x) => x.guest_id === g.guest_id).seat_group}</span>}
              </div>
            ))}
            {data.unassigned?.length === 0 && <p className="text-sm text-[var(--muted)] text-center py-6">All guests seated 🎉</p>}
          </div>

          {data.tables.some(t => (t.assigned_guests || []).length > 0) && (
            <div className="border-t border-[var(--line)] pt-3 mt-3 max-h-[200px] overflow-y-auto">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2">Seated</p>
              {data.tables.map((t) => (t.assigned_guests || []).map((g) => (
                <div key={g.guest_id} className="text-xs flex items-center justify-between py-1">
                  <span><span className="font-medium">{g.name}</span> <span className="text-[var(--muted)]">· {t.name}</span>{details?.guests?.find((x) => x.guest_id === g.guest_id)?.dietary && <span className="text-amber-700"> · {details.guests.find((x) => x.guest_id === g.guest_id).dietary}</span>}</span>
                  <button onClick={() => unassignGuest(g.guest_id)} className="text-[var(--muted)] hover:text-red-600">×</button>
                </div>
              )))}
            </div>
          )}
        </div>
      </div>

      <SeatRequests rows={requests} reload={load} />
      {details?.guests?.length > 0 && (
        <div className="bg-white border border-[var(--line)] rounded-2xl p-4 mt-6" data-testid="guest-details-panel">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-1">Meals, dietary needs & keep-together groups</p>
          <p className="text-xs text-[var(--muted)] mb-2">Give guests the same group name (e.g. "Smith family") and they're seated together when you drag one of them.</p>
          <div className="grid md:grid-cols-2 gap-x-6 max-h-[420px] overflow-y-auto">
            {details.guests.map((g) => <GuestDetailRow key={g.guest_id} g={g} options={details.meal_options} tableName={data.tables.find((t) => t.table_id === g.table_id)?.name} reload={load} />)}
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md">
            <div className="flex justify-between mb-6"><h3 className="font-serif text-2xl">New Table</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={add} className="space-y-4">
              <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Table Name</label><input data-testid="form-table-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-luxe" placeholder="Table 1, Family, VIP…" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Seats</label><input type="number" min="1" value={form.capacity} onChange={(e) => setForm({...form, capacity: parseInt(e.target.value)||1})} className="input-luxe" /></div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Shape</label>
                  <select value={form.shape} onChange={(e) => setForm({...form, shape: e.target.value})} className="input-luxe">{SHAPES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select>
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-[var(--muted)]"><input type="checkbox" checked={form.is_vip} onChange={(e) => setForm({...form, is_vip: e.target.checked})} /> VIP</label>
                <label className="flex items-center gap-2 text-sm text-[var(--muted)]"><input type="checkbox" checked={form.is_kids} onChange={(e) => setForm({...form, is_kids: e.target.checked})} /> Kids</label>
              </div>
              <button type="submit" disabled={busy} data-testid="form-table-submit" className="btn-gold w-full">{busy ? "Adding…" : "Add Table"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
